<template>
  <div>
    <v-container class="pa-0">
      <v-row>
        <v-col cols="12">
          <v-btn @click="handleClickAdd" small
            ><v-icon size="12" class="mr-2 btn-icon">fas fa-plus</v-icon>New
            Message</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-tabs
            v-model="tabs"
            fixed-tabs
            slider-color="black"
            color="#616162"
            height="38"
          >
            <v-tab>Sent</v-tab>
            <v-tab>Drafts</v-tab>
          </v-tabs>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="pt-0">
          <div v-if="loading">
            <Loader text="Loading messages" size="small"></Loader>
          </div>
          <v-list v-if="!loading" two-line class="pa-0 listing">
            <v-list-item-group v-model="selected" mandatory>
              <template>
                <div v-if="noContent" class="pa-2">No messages found</div>
                <div v-if="!noContent">
                  <div v-for="(message, index) in messages" :key="index">
                    <v-list-item @click="handleClickSelect(message)">
                      <template>
                        <v-list-item-content>
                          <v-list-item-title>{{
                            message.subject
                          }}</v-list-item-title>

                          <v-list-item-subtitle>
                            <em>{{ getMessageDate(message) }}</em> -
                            {{ getGroupNames(message) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-list-item>

                    <v-divider
                      v-if="index < messages.length - 1"
                      :key="index"
                    ></v-divider>
                  </div>
                </div>
              </template>
            </v-list-item-group>
          </v-list>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import lodashFind from "lodash/find";

import {
  getMessages,
  getMessage,
} from "@/services/communication/communication.service";
import { getFormatted } from "@/common/date.utilities";
import { pageTrackerLogger } from "@/common/pageTracker";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  SELECT: "select",
  ADD: "add",
};

export default {
  name: "CommsMessageListing",
  components: {
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    refresh: {
      type: String,
    },
    groups: {
      type: Array,
    },
    locations: {
      type: Array,
    },
  },
  data: () => ({
    tabs: null,
    type: null,
    selected: 0,
    messages: null,
    selectedMessage: null,
    infiniteLoaderId: new Date().getTime(),
    attemptedDataLoad: false,
    loading: false,
    page: 1,
    pageSize: 25,
  }),
  async mounted() {
    this.tabs = 0;
  },
  computed: {
    noContent() {
      return !this.loading && this.messages && this.messages.length === 0;
    },
    isDraft() {
      return this.type === "messages_draft";
    },
  },
  methods: {
    getMessageDate(message) {
      if (this.type) {
        if (this.isDraft) {
          return getFormatted(message.createdTimestamp, {
            showTimeFromNow: true,
            dateOnly: true,
          });
        } else {
          return getFormatted(message.sentTimestamp, {
            showTimeFromNow: true,
            dateOnly: true,
          });
        }
      }
      return null;
    },
    getGroupNames(message) {
      if (message) {
        const grps = [];
        if (message.groups) {
          message.groups.forEach((group) => {
            grps.push(group.groupName);
          });
        }
        if (message.locationIds) {
          message.locationIds.forEach((locationId) => {
            const location = lodashFind(this.locations, ["id", locationId]);
            if (location && location.name) {
              grps.push(location.name);
            }
          });
        }
        return grps.join(", ");
      }
      return null;
    },
    async getMessages(params, state) {
      this.loading = true;
      this.messages = await getMessages({
        draft: this.isDraft,
      });

      if (this.messages && this.messages.length && this.messages[0].messageId) {
        await this.setMessage(this.messages[0].messageId);
        this.selected = 0;
        this.loading = false;
      } else {
        this.$emit(EMITTED_ACTIONS.SELECT, null);
        this.loading = false;
      }
    },
    async setMessage(messageId) {
      if (messageId) {
        const message = await getMessage({ messageId, noCache: this.isDraft });
        if (message) {
          this.selectedMessage = message;
          this.$emit(EMITTED_ACTIONS.SELECT, this.selectedMessage);
        }
      }
    },
    async handleClickSelect(message) {
      await this.setMessage(message.messageId);
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.mass_communication,
        EVENT_ACTIONS.clicked,
        "View a Message"
      );
    },
    handleClickAdd() {
      this.selectedMessage = null;
      this.$emit(EMITTED_ACTIONS.ADD);
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.mass_communication,
        EVENT_ACTIONS.clicked,
        "New Message"
      );
    },
    handlePageTrackerEvent(category, action, name) {
      let _paq = (window._paq = window._paq || []);
      _paq.push(["trackEvent", category, action, name]);
      pageTrackerLogger(
        "Page Tracker Event " +
          category +
          " " +
          action +
          " " +
          name +
          " logged."
      );
    },
  },
  watch: {
    async tabs() {
      this.$emit(EMITTED_ACTIONS.SELECT, null);
      this.type = this.tabs === 1 ? "messages_draft" : "messages_sent";
      await this.getMessages();
    },
    async refresh() {
      this.$emit(EMITTED_ACTIONS.SELECT, null);
      await this.getMessages();
    },
  },
  beforeDestroy() {
    this.selected = 0;
    this.messages = null;
    this.selectedMessage = null;
    this.type = null;
    this.tabs = null;
  },
};
</script>

<style>
.theme--light.v-tabs > .v-tabs-bar {
  background-color: transparent !important;
}
</style>

<style scoped>
.item-icon {
  position: relative;
  left: 15px;
  top: 2px;
}
.hyp-tabs-slider {
  background-color: black !important;
  border-color: black !important;
}
.btn-icon {
  position: relative;
  top: 1px;
}
.listing {
  height: 65vh;
  overflow-y: auto;
}
</style>
